import { Timestamp } from 'firebase/firestore';

/**
 * Safely converts any value to a number
 * @param value - Value to convert
 * @param defaultValue - Default value to return if conversion fails
 * @returns Converted number or default value
 */
export const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (value === undefined || value === null) return defaultValue;
  
  // If already a number, return directly (but not NaN)
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  
  // If string, support various formats
  if (typeof value === 'string') {
    try {
      // Remove all non-numeric characters except point and comma
      const cleanValue = value.replace(/[^\d.,]/g, '');
      
      // If comma is used as decimal separator, convert to point
      // But only if no point before the comma (otherwise thousands separator)
      const normalizedValue = cleanValue.includes('.') && cleanValue.includes(',') 
        ? cleanValue.replace(/\./g, '').replace(',', '.') // e.g. "1.234,56" -> "1234.56"
        : cleanValue.replace(',', '.'); // e.g. "1234,56" -> "1234.56"
      
      const parsed = parseFloat(normalizedValue);
      if (!isNaN(parsed)) {
        return parsed;
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error parsing string value:', value, e);
      }
      return defaultValue;
    }
  }
  
  // Fallback for invalid values
  if (process.env.NODE_ENV === 'development') {
    console.warn('Invalid value for number conversion:', value, typeof value);
  }
  return defaultValue;
};

/**
 * Normalizes timestamps from various formats to Date objects
 * @param date - Date in various formats (Timestamp, Date, string, number)
 * @returns Normalized Date object
 */
export const normalizeTimestamp = (date: Date | number | string | any): Date => {
  try {
    let normalizedDate: Date;
    
    // If it's a Firestore Timestamp
    if (date && typeof date.toDate === 'function') {
      normalizedDate = date.toDate();
    }
    // If it's already a Date object
    else if (date instanceof Date) {
      normalizedDate = date;
    }
    // If it's a string or number
    else if (typeof date === 'string' || typeof date === 'number') {
      normalizedDate = new Date(date);
      if (isNaN(normalizedDate.getTime())) {
        throw new Error('Invalid date: ' + date);
      }
    }
    else {
      throw new Error('Invalid date type: ' + (typeof date));
    }
    
    return normalizedDate;
  } catch (error) {
    console.error('Error normalizing timestamp:', error);
    return new Date(); // Fallback to current time
  }
};

/**
 * Safely extracts a date value from a workflow stage
 * @param stage - The workflow stage
 * @param field - The field to extract (documentDate or createdAt)
 * @returns Timestamp in milliseconds
 */
export const getDateValue = (stage: any, field: 'documentDate' | 'createdAt'): number => {
  try {
    const value = stage[field]; // Get value once

    if (!value) {
      return 0;
    }

    // 1. Check for Firestore Timestamp
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
      return value.toDate().getTime();
    }
    // 2. Check for JavaScript Date Object
    else if (value instanceof Date) {
      return value.getTime();
    }
    // 3. Check for string
    else if (typeof value === 'string') {
      const dateFromString = new Date(value);
      // Check if date is valid
      return isNaN(dateFromString.getTime()) ? 0 : dateFromString.getTime();
    }
    // 4. Check for number (Unix timestamp in ms)
    else if (typeof value === 'number') {
      return value;
    }

    // Fallback for unknown formats
    console.warn(`Unknown date format for field '${field}':`, value);
    return 0;
  } catch (e) {
    console.error(`Error processing date field '${field}':`, e);
    return 0;
  }
};

/**
 * Generates a server timestamp for Firebase
 * @returns Current timestamp
 */
export const getCurrentTimestamp = (): Timestamp => {
  return Timestamp.now();
}; 